import React, { useEffect, useState } from 'react';
import LoadError from '../../components/LoadError';
import { apiGet, apiPut } from '../../contexts/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { UserCircle, Users, Ban, CheckCircle, KeyRound, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

export default function AllPartners() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [resetDialog, setResetDialog] = useState(false);
  const [resetPartner, setResetPartner] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchPartners = () => {
    setLoadError(false);
    apiGet('/admin/partners').then(r => setPartners(r.data)).catch(() => setLoadError(true)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchPartners(); }, []);

  const [togglingId, setTogglingId] = useState(null);
  const toggleActive = async (partner) => {
    // Each click flips the account and e-mails the partner, so ask first and ignore repeat clicks.
    if (togglingId) return;
    if (partner.is_active !== false && !window.confirm(`Disable ${partner.name || partner.email}? They will no longer be able to sign in.`)) return;
    setTogglingId(partner._id);
    try {
      const { data } = await apiPut(`/admin/partners/${partner._id}/toggle-active`);
      toast.success(data.is_active ? 'Partner activated' : 'Partner deactivated');
      fetchPartners();
    } catch (err) { toast.error('Failed to update'); }
    finally { setTogglingId(null); }
  };

  const openResetDialog = (partner) => {
    setResetPartner(partner);
    setNewPassword('');
    setShowPassword(false);
    setResetDialog(true);
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setResetting(true);
    try {
      await apiPut(`/admin/partners/${resetPartner._id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${resetPartner.name}`);
      setResetDialog(false);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reset password'); }
    finally { setResetting(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-3 border-brand border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="all-partners-page">
      <div>
        <h1 className="text-3xl font-semibold text-ember tracking-tight" style={{ fontFamily: 'Outfit' }}>Partners</h1>
        <p className="text-ink-2 mt-1">All registered partner organizations</p>
      </div>

      {loadError ? (
        <LoadError what="the partner list" onRetry={() => { setLoading(true); fetchPartners(); }} />
      ) : partners.length === 0 ? (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 p-12 text-center">
          <UserCircle className="w-12 h-12 text-ink-3 mx-auto mb-4" />
          <p className="text-ink-2">No partners registered yet</p>
        </div>
      ) : (
        <div className="bg-surface/70 backdrop-blur-xl rounded-xl border border-contrast/10 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="partners-table">
              <thead>
                <tr className="bg-contrast/5 border-b border-contrast/10">
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Partner</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Organization</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Email</th>
                  <th className="text-left py-3 px-4 font-medium text-ink-2">Phone</th>
                  <th className="text-center py-3 px-4 font-medium text-ink-2">Candidates</th>
                  <th className="text-center py-3 px-4 font-medium text-ink-2">Status</th>
                  <th className="text-right py-3 px-4 font-medium text-ink-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {partners.map((p) => (
                  <tr key={p._id} className="border-b border-contrast/[0.07] hover:bg-contrast/5" data-testid={`partner-row-${p._id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 bg-surface-3 rounded-full flex items-center justify-center text-sm font-medium text-brand">
                          {p.name?.charAt(0)?.toUpperCase()}
                        </div>
                        <span className="font-medium text-ink">{p.name}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-ink-2">{p.organization || 'N/A'}</td>
                    <td className="py-3 px-4 text-ink-2">{p.email}</td>
                    <td className="py-3 px-4 text-ink-2 whitespace-nowrap">{p.phone || 'N/A'}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-contrast/[0.07] text-ink rounded-full text-xs font-medium">
                        <Users className="w-3 h-3" /> {p.candidate_count}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${p.is_active !== false ? 'bg-brand/10 text-success' : 'bg-ember/10 text-danger'}`}>
                        {p.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(p)}
                          disabled={togglingId === p._id}
                          data-testid={`toggle-partner-${p._id}`}
                          className={p.is_active !== false ? 'text-ember hover:text-danger hover:bg-ember/10' : 'text-success hover:text-success hover:bg-brand/10'}
                        >
                          {p.is_active !== false ? <><Ban className="w-4 h-4 mr-1" /> Disable</> : <><CheckCircle className="w-4 h-4 mr-1" /> Enable</>}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openResetDialog(p)}
                          data-testid={`reset-pwd-${p._id}`}
                          className="text-brand hover:text-brand-soft hover:bg-surface-3"
                        >
                          <KeyRound className="w-4 h-4 mr-1" /> Reset Password
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Reset Password Dialog */}
      <Dialog open={resetDialog} onOpenChange={setResetDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit' }}>Reset Password</DialogTitle>
          </DialogHeader>
          {resetPartner && (
            <div className="space-y-4">
              <p className="text-sm text-ink-2">
                Set a new password for <span className="font-semibold text-ink">{resetPartner.name}</span> ({resetPartner.email})
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input
                    id="new-password"
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Min 6 characters"
                    className="h-11 border-contrast/10 pr-10"
                    data-testid="reset-password-input"
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} aria-label={showPassword ? 'Hide password' : 'Show password'} className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-3 hover:text-ink-2">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setResetDialog(false)}>Cancel</Button>
                <Button onClick={handleResetPassword} disabled={resetting} className="bg-brand hover:bg-brand-soft text-white" data-testid="reset-password-submit">
                  {resetting ? 'Resetting...' : 'Reset Password'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
